import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Divider } from 'semantic-ui-react';
import styles from './ReadOnlyInspectionForm.module.scss'; // Usar estilos propios

const ReadOnlyInspectionForm = React.memo(({ formSchema, responseData, signatureData }) => {
  const sections = formSchema?.sections || [];

  const renderField = field => {
    const value = responseData[field.name];
    const fieldKey = field.id || field.name;

    // Verificar lógica condicional (si aplica)
    if (field.conditional) {
      const conditionField = field.conditional.field;
      const conditionValue = field.conditional.value;
      const conditionOperator = field.conditional.operator;
      const currentValue = responseData[conditionField];

      let showField = false;
      switch (conditionOperator) {
        case '===':
          showField = currentValue === conditionValue;
          break;
        case '!==':
          showField = currentValue !== conditionValue;
          break;
        default:
          showField = true;
      }

      if (!showField) {
        return null;
      }
    }

    switch (field.type) {
      case 'text':
      case 'number':
      case 'date':
      case 'time':
      case 'variable':
      case 'wheelbase_vehicle':
      case 'wheelbase_booking':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            <input type="text" value={value || ''} readOnly className={styles.readOnlyInput} />
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            <textarea
              value={value || ''}
              readOnly
              rows={field.rows || 3}
              className={styles.readOnlyInput}
            />
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            <div className={styles.readOnlyValue}>
              {value || <span className={styles.emptyValue}>- No selection -</span>}
            </div>
          </div>
        );

      case 'radio':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            <div className={styles.radioGroup}>
              {field.options?.map(option => (
                <label key={option} className={`${styles.radioOption} ${styles.readOnlyOption}`}>
                  <input type="radio" checked={value === option} disabled />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            <div className={styles.checkboxGroup}>
              {field.options?.map(option => {
                const isChecked = Array.isArray(value) ? value.includes(option) : value === option;
                return (
                  <label
                    key={option}
                    className={`${styles.checkboxOption} ${styles.readOnlyOption}`}
                  >
                    <input type="checkbox" checked={isChecked} disabled />
                    <span>{option}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'camera':
      case 'file': {
        const images = Array.isArray(value) ? value : value ? [value] : [];
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            {images.length > 0 ? (
              <div className={styles.photoPreviewContainer}>
                {images.map((imgUrl, idx) => (
                  <div key={idx} className={styles.photoPreview}>
                    <a href={imgUrl} target="_blank" rel="noopener noreferrer">
                      <img src={imgUrl} alt={`Evidence ${idx + 1}`} />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyValue}>No files uploaded</div>
            )}
          </div>
        );
      }

      case 'signature': {
        // La firma puede venir en el campo específico o en signatureData global si es legacy
        const signatureUrl = value || signatureData?.[field.name];
        return (
          <div key={fieldKey} className={styles.field}>
            <label>{field.label}</label>
            {signatureUrl ? (
              <div className={styles.signaturePreview}>
                <img
                  src={signatureUrl}
                  alt="Signature"
                  style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #ddd' }}
                />
              </div>
            ) : (
              <div className={styles.emptyValue}>No signature</div>
            )}
          </div>
        );
      }

      case 'static_text':
        return (
          <div
            key={fieldKey}
            className={styles.staticText}
            dangerouslySetInnerHTML={{ __html: field.formattedText || field.label }}
          />
        );

      case 'link': {
        const url = field.url || field.label;
        const idMatch = url && url.match(/\/d\/([-\w]+)/);
        const fileId = idMatch ? idMatch[1] : null;
        const isGoogleDrive = url && url.includes('drive.google.com');

        return (
          <div key={fieldKey} className={styles.field}>
            <div className={styles.linkContainer}>
              <Icon name="linkify" />
              <a href={url} target="_blank" rel="noopener noreferrer">
                {field.label || field.url}
              </a>
            </div>
            {isGoogleDrive && fileId && (
              <div className={styles.linkImagePreview}>
                <img
                  src={`https://drive.google.com/thumbnail?id=${fileId}&sz=w640`}
                  alt="Preview"
                  referrerPolicy="no-referrer"
                  style={{
                    width: '300px',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    marginTop: '10px',
                    display: 'block',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={styles.readOnlyForm}>
      {sections.map((section, idx) => (
        <div key={idx} className={styles.sectionContainer}>
          <h4 className={styles.sectionTitle}>{section.title}</h4>
          <div className={styles.fieldsContainer}>
            {(section.fields || [])
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(field => renderField(field))}
          </div>
          {idx < sections.length - 1 && <Divider />}
        </div>
      ))}
    </div>
  );
});

ReadOnlyInspectionForm.propTypes = {
  formSchema: PropTypes.object.isRequired,
  responseData: PropTypes.object.isRequired,
  signatureData: PropTypes.object,
};

export default ReadOnlyInspectionForm;
